import React, { useEffect } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { useForm, Controller } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import { Button, Card, CardHeader, CardContent } from '../../components/common';
import { Input } from '../../components/forms';
import { registerSchema, RegisterFormData } from '../../schemas';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { registerUser } from '../../store/slices/authSlice';
import { UserRole } from '../../types';

export const RegisterPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { isLoading, isAuthenticated } = useAppSelector(state => state.auth);

  const {
    handleSubmit,
    control,
    formState: { errors, isSubmitting },
  } = useForm<RegisterFormData>({
    resolver: yupResolver(registerSchema),
    mode: 'all',
    defaultValues: {
      email: '',
      password: '',
      confirmPassword: '',
      firstName: '',
      lastName: '',
      role: UserRole.TEACHER,
      agreeToTerms: false,
    },
  });

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  const onSubmit = async (
    data: RegisterFormData,
    event?: React.BaseSyntheticEvent
  ) => {
    if (event) {
      event.preventDefault();
    }
    try {
      const registerData = {
        username: data.email, // Use email as username
        email: data.email,
        full_name: `${data.firstName} ${data.lastName}`?.trim() || '',
        password: data.password,
        role: data.role.toLowerCase(),
      };

      await dispatch(registerUser(registerData)).unwrap();

      // Show success toast and redirect
      toast.success('Account created successfully! Please log in to continue.');
      navigate('/login', { replace: true });
    } catch (err: unknown) {
      // Show error toast
      toast.error(
        err instanceof Error
          ? err.message
          : typeof err === 'string'
            ? err
            : 'Registration failed'
      );
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <Card className="shadow-lg">
          <CardHeader>
            <h1 className="text-2xl font-bold text-center text-gray-900">
              K-12 Admin Assistant
            </h1>
            <p className="mt-2 text-sm text-gray-600 text-center">
              K12 Education Platform
            </p>
          </CardHeader>
          <CardContent>
            <form
              onSubmit={handleSubmit(onSubmit)}
              className="space-y-4"
              noValidate
            >
              <h2 className="text-lg text-center font-semibold text-gray-900">
                Register
              </h2>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Controller
                    name="firstName"
                    control={control}
                    render={({ field }) => (
                      <Input
                        label="First Name"
                        {...field}
                        placeholder="First name"
                      />
                    )}
                  />
                  {errors.firstName && (
                    <p className="mt-1 text-sm text-red-600">
                      {errors.firstName.message}
                    </p>
                  )}
                </div>

                <div>
                  <Controller
                    name="lastName"
                    control={control}
                    render={({ field }) => (
                      <Input
                        label="Last Name"
                        {...field}
                        placeholder="Last name"
                      />
                    )}
                  />
                  {errors.lastName && (
                    <p className="mt-1 text-sm text-red-600">
                      {errors.lastName.message}
                    </p>
                  )}
                </div>
              </div>

              <div>
                <Controller
                  name="email"
                  control={control}
                  render={({ field }) => (
                    <Input
                      label="Email"
                      type="email"
                      {...field}
                      placeholder="Enter your email"
                    />
                  )}
                />
                {errors.email && (
                  <p className="mt-1 text-sm text-red-600">
                    {errors.email.message}
                  </p>
                )}
              </div>

              <div>
                <Controller
                  name="password"
                  control={control}
                  render={({ field }) => (
                    <Input
                      label="Password"
                      type="password"
                      {...field}
                      placeholder="Enter your password"
                    />
                  )}
                />
                {errors.password && (
                  <p className="mt-1 text-sm text-red-600">
                    {errors.password.message}
                  </p>
                )}
              </div>

              <div>
                <Controller
                  name="confirmPassword"
                  control={control}
                  render={({ field }) => (
                    <Input
                      label="Confirm Password"
                      type="password"
                      {...field}
                      placeholder="Confirm your password"
                    />
                  )}
                />
                {errors.confirmPassword && (
                  <p className="mt-1 text-sm text-red-600">
                    {errors.confirmPassword.message}
                  </p>
                )}
              </div>

              <div>
                <label
                  htmlFor="role-select"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Role
                </label>
                <Controller
                  name="role"
                  control={control}
                  render={({ field }) => (
                    <select
                      id="role-select"
                      {...field}
                      className="w-full h-10 px-3 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-gray-900 bg-white transition-colors duration-200"
                    >
                      <option value={UserRole.TEACHER}>Teacher</option>
                      <option value={UserRole.PARENT}>Parent / Guardian</option>
                    </select>
                  )}
                />
                {errors.role && (
                  <p className="mt-1 text-sm text-red-600">
                    {errors.role.message}
                  </p>
                )}
              </div>

              <div>
                <div className="flex items-center">
                  <Controller
                    name="agreeToTerms"
                    control={control}
                    render={({ field }) => (
                      <input
                        type="checkbox"
                        checked={field.value}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        name={field.name}
                        ref={field.ref}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                    )}
                  />
                  <label className="ml-2 block text-sm text-gray-900">
                    I agree to the{' '}
                    <button
                      type="button"
                      className="text-blue-600 hover:text-blue-800 underline"
                      onClick={() => {
                        // Terms and conditions functionality
                      }}
                    >
                      Terms and Conditions
                    </button>
                  </label>
                </div>
                {errors.agreeToTerms && (
                  <p className="mt-1 text-sm text-red-600">
                    {errors.agreeToTerms.message}
                  </p>
                )}
              </div>

              <Button
                type="submit"
                loading={isSubmitting || isLoading}
                className="w-full"
              >
                Register
              </Button>

              <div className="text-center">
                <button
                  type="button"
                  onClick={() => navigate('/login')}
                  className="text-blue-600 hover:text-blue-800 text-sm"
                >
                  Already have an account? Sign in here
                </button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
